import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUrlState } from "@/lib/navState";
import { api } from "@/lib/api";
import { label, formatDate } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, Clock, ChevronRight, Wrench, Search, Bell, AlertTriangle, CheckCircle2 } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="ti-shell -mx-5 -my-5 min-h-[calc(100vh-3.25rem)] px-4 py-5 sm:-mx-8 sm:-my-8 sm:px-6 sm:py-6">
      <div className="mx-auto w-full max-w-[1800px]">{children}</div>
    </div>
  );
}

const TONE = {
  RED: { text: "text-red-400", dot: "bg-red-500", bar: "bg-red-500", chip: "bg-red-500/15 text-red-300", label: "Overdue" },
  YELLOW: { text: "text-amber-400", dot: "bg-amber-400", bar: "bg-amber-400", chip: "bg-amber-400/15 text-amber-300", label: "Warning" },
  GREEN: { text: "text-emerald-400", dot: "bg-emerald-400", bar: "bg-emerald-400", chip: "bg-emerald-500/15 text-emerald-300", label: "On Track" },
  READY: { text: "text-emerald-300", dot: "bg-emerald-300", bar: "bg-emerald-400", chip: "bg-emerald-500/15 text-emerald-200", label: "Ready" },
  NO_TARGET: { text: "text-slate-300", dot: "bg-slate-400", bar: "bg-slate-400", chip: "bg-slate-500/20 text-slate-300", label: "No Target" },
};
const tone = (r) => TONE[r.prod_status] || TONE.NO_TARGET;
const PRIORITY = { RED: 0, YELLOW: 1, NO_TARGET: 2, GREEN: 3, READY: 4 };

function bottleneck(r) {
  if (r.prod_status === "RED") return { t: "Overdue Process", c: "text-red-400" };
  if (r.prod_status === "READY") return { t: "Ready Delivery", c: "text-emerald-300" };
  if (r.prod_status === "NO_TARGET") return { t: "Target belum diset", c: "text-amber-300" };
  if (r.prod_status === "YELLOW") return { t: "Mendekati Deadline", c: "text-amber-300" };
  return { t: "On Track", c: "text-emerald-400" };
}
function etaText(r) {
  if (r.prod_status === "READY") return "Ready";
  if (!r.target_finish_date) return "Target -";
  if (r.prod_status === "RED") return `Overdue ${r.aging_days || ""}h`.trim();
  return "On Time";
}

function Kpi({ label: l, value, sub, accent, bar }) {
  return (
    <div className="ti-card flex flex-col justify-between p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider ti-muted">{l}</div>
      <div className={`mt-1 font-heading text-2xl font-extrabold leading-none ${accent || "text-white"}`}>{value}</div>
      {bar != null ? (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full ti-progress"><span className="block h-full rounded-full bg-[#3B82F6]" style={{ width: `${bar}%` }} /></div>
      ) : <div className="mt-1 text-[11px] ti-muted">{sub}</div>}
    </div>
  );
}

export default function LiveProductionBoard() {
  const [rows, setRows] = useState(null);
  const [pics, setPics] = useState([]);
  const [q, setQ] = useUrlState("q", "");
  const [status, setStatus] = useUrlState("status", "ALL");
  const [foreman, setForeman] = useUrlState("foreman", "ALL");
  const [warnOnly, setWarnOnly] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/production/schedule").then((r) => { setRows(r.data.rows || []); setPics(r.data.pics || []); }).catch(() => setRows([]));
  }, []);

  const filtered = useMemo(() => {
    let out = (rows || []).filter((r) => {
      if (status !== "ALL" && r.prod_status !== status) return false;
      if (foreman !== "ALL" && (r.pic || "") !== foreman) return false;
      if (warnOnly && !["RED", "YELLOW"].includes(r.prod_status)) return false;
      if (q) {
        const s = q.toLowerCase();
        if (![r.license_plate, r.vehicle, r.wo_number, r.customer_name].some((x) => (x || "").toLowerCase().includes(s))) return false;
      }
      return true;
    });
    return out.sort((a, b) => (PRIORITY[a.prod_status] ?? 5) - (PRIORITY[b.prod_status] ?? 5) || b.progress - a.progress);
  }, [rows, status, foreman, warnOnly, q]);

  const kpi = useMemo(() => {
    const t = filtered.length;
    const on = filtered.filter((r) => r.prod_status === "GREEN").length;
    const warn = filtered.filter((r) => ["YELLOW", "NO_TARGET"].includes(r.prod_status)).length;
    const over = filtered.filter((r) => r.prod_status === "RED").length;
    const ready = filtered.filter((r) => r.prod_status === "READY").length;
    const avg = t ? Math.round(filtered.reduce((s, r) => s + (r.progress || 0), 0) / t) : 0;
    const pct = (n) => (t ? Math.round((n / t) * 100) : 0);
    return { t, on, warn, over, ready, avg, pct };
  }, [filtered]);

  const dist = useMemo(() => {
    const b = [0, 0, 0, 0];
    filtered.forEach((r) => { const p = r.progress || 0; b[p <= 25 ? 0 : p <= 50 ? 1 : p <= 75 ? 2 : 3]++; });
    return b;
  }, [filtered]);
  const distLabels = ["0–25%", "26–50%", "51–75%", "76–100%"];

  const now = new Date();

  return (
    <Shell>
      {/* HEADER */}
      <div className="ti-card flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#3B82F6] font-heading text-sm font-extrabold text-[#0A101F]">TW</div>
          <div><div className="font-heading text-sm font-bold text-white">TITANIUM</div><div className="text-[10px] uppercase tracking-wider ti-muted">Workshop System</div></div>
        </div>
        <div className="text-center">
          <h1 className="font-heading text-xl font-extrabold text-white sm:text-2xl">Live Production Board</h1>
          <p className="text-[11px] ti-muted">Monitor Real-time Seluruh Unit di Workshop</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative"><Bell size={18} className="ti-muted" />{kpi.over > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">{kpi.over}</span>}</div>
          <div className="hidden text-right sm:block"><div className="font-mono text-sm font-semibold text-white">{now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</div><div className="text-[10px] ti-muted">{now.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}</div></div>
          <span className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-300"><span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" /> LIVE</span>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="prod-filter-status" className="h-9 sm:w-44"><SelectValue placeholder="Semua Status" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">Semua Status</SelectItem><SelectItem value="RED">Overdue</SelectItem><SelectItem value="YELLOW">Warning</SelectItem><SelectItem value="GREEN">On Track</SelectItem><SelectItem value="READY">Ready Delivery</SelectItem><SelectItem value="NO_TARGET">Tanpa Target</SelectItem></SelectContent>
        </Select>
        <Select value={foreman} onValueChange={setForeman}>
          <SelectTrigger data-testid="prod-filter-foreman" className="h-9 sm:w-44"><SelectValue placeholder="Semua Foreman" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">Semua Foreman</SelectItem>{pics.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <button data-testid="prod-filter-warn" onClick={() => setWarnOnly((v) => !v)} className={`h-9 rounded-md px-3 text-xs font-semibold transition-colors ${warnOnly ? "bg-red-500/20 text-red-300" : "ti-card ti-muted"}`}>Warning / Overdue</button>
        <div className="relative col-span-2 sm:ml-auto sm:min-w-[240px] sm:flex-1">
          <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 ti-muted" />
          <Input data-testid="prod-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Unit / Nopol / WO / Customer" className="h-9 pl-8" />
        </div>
      </div>

      {/* KPI */}
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <Kpi label="Total Unit" value={kpi.t} sub="Unit Aktif" />
        <Kpi label="On Time" value={kpi.on} sub={`${kpi.pct(kpi.on)}%`} accent="text-emerald-400" />
        <Kpi label="Warning" value={kpi.warn} sub={`${kpi.pct(kpi.warn)}%`} accent="text-amber-400" />
        <Kpi label="Overdue" value={kpi.over} sub={`${kpi.pct(kpi.over)}%`} accent="text-red-400" />
        <Kpi label="Avg Progress" value={`${kpi.avg}%`} bar={kpi.avg} />
      </div>

      {rows && rows.length === 0 && (
        <div data-testid="production-empty" className="ti-card mt-4 flex flex-col items-center gap-2 py-16 text-center">
          <Wrench size={30} className="ti-muted" /><p className="font-heading text-base font-semibold text-white">Belum Ada Unit di Produksi</p>
          <p className="text-sm ti-muted">Unit muncul setelah masuk bengkel (Unit In).</p>
        </div>
      )}

      {/* PRODUCTION TABLE (desktop/tablet) */}
      {rows && rows.length > 0 && (
        <div className="ti-card mt-3 hidden overflow-hidden md:block">
          <div className="max-h-[62vh] overflow-y-auto">
            <table className="w-full text-left">
              <thead className="sticky top-0 z-10 bg-[#111a2e] text-[10px] uppercase tracking-wider ti-muted">
                <tr>{["Unit / Customer", "Status / Progress", "Foreman", "ETA / Aging", "Bottleneck", ""].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((r) => {
                  const tn = tone(r), bn = bottleneck(r);
                  return (
                    <tr key={r.id} data-testid={`production-row-${r.id}`} onClick={() => navigate(`/tracking/${r.id}`)} className="cursor-pointer transition-colors hover:bg-white/5">
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2"><span className="ti-plate text-[11px]">{r.license_plate}</span><span className="text-sm font-semibold text-white">{r.vehicle || "—"}</span></div>
                        <div className="mt-0.5 text-[11px] ti-muted">{r.customer_name || "—"} · <span className="font-mono">{r.wo_number}</span></div>
                      </td>
                      <td className="px-3 py-2.5 w-[260px]">
                        <div className="flex items-center justify-between text-[11px]"><span className={`flex items-center gap-1.5 font-semibold ${tn.text}`}><span className={`h-1.5 w-1.5 rounded-full ${tn.dot}`} />{label(r.stage)}</span><span className="ti-muted">{r.stage_index + 1}/{r.total_stages}</span></div>
                        <div className="mt-1.5 flex items-center gap-2"><div className="h-1.5 flex-1 overflow-hidden rounded-full ti-progress"><span className={`block h-full rounded-full ${tn.bar}`} style={{ width: `${r.progress}%` }} /></div><span className="w-8 text-right text-[11px] font-semibold text-white">{r.progress}%</span></div>
                      </td>
                      <td className="px-3 py-2.5 text-[12px]">{r.pic ? <span className="text-white">{r.pic}</span> : <span className="ti-muted">Belum ditugaskan</span>}</td>
                      <td className="px-3 py-2.5 text-[11px]">
                        <div className="text-white">{r.target_finish_date ? formatDate(r.target_finish_date) : "—"}</div>
                        <div className={`mt-0.5 font-semibold ${tn.text}`}>{etaText(r)}{r.aging_days ? ` · ${r.aging_days}h` : ""}</div>
                      </td>
                      <td className="px-3 py-2.5"><span className={`text-[12px] font-semibold ${bn.c}`}>{bn.t}</span></td>
                      <td className="px-3 py-2.5 text-right"><ChevronRight size={16} className="ti-muted" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MOBILE COMPACT ROWS */}
      {rows && rows.length > 0 && (
        <div className="mt-3 space-y-2 md:hidden">
          {filtered.map((r) => {
            const tn = tone(r), bn = bottleneck(r);
            return (
              <button key={r.id} data-testid={`production-card-${r.id}`} onClick={() => navigate(`/tracking/${r.id}`)} className="ti-card ti-card-hover flex w-full flex-col gap-1.5 p-3 text-left">
                <div className="flex items-center justify-between"><div className="flex items-center gap-2"><span className="ti-plate text-[11px]">{r.license_plate}</span><span className="text-sm font-semibold text-white">{r.vehicle || "—"}</span></div><ChevronRight size={15} className="ti-muted" /></div>
                <div className="flex items-center justify-between text-[11px]"><span className={`flex items-center gap-1.5 font-semibold ${tn.text}`}><span className={`h-1.5 w-1.5 rounded-full ${tn.dot}`} />{label(r.stage)}</span><span className="ti-muted">{r.stage_index + 1}/{r.total_stages}</span></div>
                <div className="flex items-center gap-2"><div className="h-1.5 flex-1 overflow-hidden rounded-full ti-progress"><span className={`block h-full rounded-full ${tn.bar}`} style={{ width: `${r.progress}%` }} /></div><span className="text-[11px] font-semibold text-white">{r.progress}%</span></div>
                <div className="flex items-center justify-between text-[10px] ti-muted"><span>{r.insurance_company} · <span className={bn.c}>{bn.t}</span></span><span className="flex items-center gap-1"><Clock size={11} />{r.aging_days || 0}h · {r.target_finish_date ? formatDate(r.target_finish_date) : "-"}</span></div>
              </button>
            );
          })}
        </div>
      )}

      {/* BOTTLENECK SUMMARY + PROGRESS DISTRIBUTION */}
      {rows && rows.length > 0 && (
        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="ti-card p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ti-gold"><AlertTriangle size={13} /> Ringkasan Bottleneck</div>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[["Overdue", kpi.over, "text-red-400"], ["Warning", kpi.warn, "text-amber-400"], ["On Track", kpi.on, "text-emerald-400"], ["Ready", kpi.ready, "text-emerald-300"]].map(([l, v, c]) => (
                <div key={l} className="rounded-md bg-white/5 p-3"><div className={`font-heading text-xl font-extrabold ${c}`}>{v}</div><div className="text-[10px] ti-muted">{l} Unit</div></div>
              ))}
            </div>
          </div>
          <div className="ti-card p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ti-gold"><CheckCircle2 size={13} /> Distribusi Progress</div>
            <div className="mt-3 space-y-2">
              {dist.map((n, i) => { const pct = kpi.t ? Math.round((n / kpi.t) * 100) : 0; return (
                <div key={i} className="flex items-center gap-3 text-[11px]">
                  <span className="w-16 ti-muted">{distLabels[i]}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full ti-progress"><span className="block h-full rounded-full bg-[#3B82F6]" style={{ width: `${pct}%` }} /></div>
                  <span className="w-16 text-right font-semibold text-white">{n} Unit</span>
                </div>
              ); })}
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
